import { useMutation, useQueryClient } from "@tanstack/react-query";
import { bridgeApiService } from "@/services/bridge-api.service";
import { BRIDGE_ASSETS } from "@/config";
import { CreateExchangeRequest, BridgeSwapParams } from "@/types/bridge.types";

export const useBridgeSwap = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      fromNetwork,
      toNetwork,
      amount,
      fromAddress,
      toAddress,
      rateId,
    }: BridgeSwapParams) => {
      // Validation
      if (!amount || parseFloat(amount) <= 0) {
        throw new Error("Please enter a valid amount greater than 0");
      }

      if (!fromAddress || !toAddress) {
        throw new Error(
          "Wallet addresses are required. Please connect both wallets."
        );
      }

      const request: CreateExchangeRequest = {
        assetFrom: BRIDGE_ASSETS.A1X,
        networkFrom: fromNetwork,
        assetTo: BRIDGE_ASSETS.A1X,
        networkTo: toNetwork,
        amountFrom: amount,
        addressTo: toAddress,
        rateId,
        // partnerFeePercent: 0.5, // 0.5% partner fee
      };

      try {
        return await bridgeApiService.createExchange(request);
      } catch (error) {
        // Enhanced error handling
        if (error instanceof Error) {
          if (error.message.includes("timeout")) {
            throw new Error("Transaction timed out. Please try again.");
          }
          if (error.message.includes("Network Error")) {
            throw new Error(
              "Network connection failed. Please check your internet and try again."
            );
          }
          if (error.message.includes("insufficient")) {
            throw new Error(
              "Insufficient balance to complete this transaction."
            );
          }
          if (
            error.message.includes("rate expired") ||
            error.message.includes("rate invalid")
          ) {
            throw new Error(
              "Exchange rate has expired. Please get a new quote and try again."
            );
          }
          if (error.message.includes("500")) {
            throw new Error(
              "Bridge service temporarily unavailable. Please try again in a moment."
            );
          }
          if (error.message.includes("400")) {
            // Check for specific validation errors
            if (
              error.message.includes("amountFrom_to_small") ||
              error.message.includes("amountTo_to_small")
            ) {
              throw new Error(
                "Amount is below the minimum allowed. Please increase the amount and try again."
              );
            }
            if (
              error.message.includes("amountFrom_to_big") ||
              error.message.includes("amountTo_to_big")
            ) {
              throw new Error(
                "Amount exceeds the maximum allowed. Please reduce the amount and try again."
              );
            }
            throw new Error(
              "Invalid transaction parameters. Please check your inputs and try again."
            );
          }
        }
        throw error;
      }
    },
    onSuccess: () => {
      // Invalidate relevant queries after successful swap
      // queryClient.invalidateQueries({ queryKey: ["bridge-estimation"] });
      // queryClient.invalidateQueries({ queryKey: ["account-tokens"] });
    },
  });
};
